import React, { useState } from 'react';
import { Box, Stack, Typography, IconButton, alpha } from '@mui/material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid-pro';
import { FlagIcon } from 'src/components/iconify/flagIcon';
import { titleCase } from 'src/utils/change-case';
import SvgColor from 'src/components/svg-color';
import countriesJson from 'src/utils/countries.json';
import CustomTooltip from '../components/CustomTooltip';
import Iconify from '../../iconify';
import DomainPill from '../components/DomainPill';
import CompetitorModal from '../components/competitor-modal';
import CompetitiveLandscapeCell from '../components/CompetitiveLandscapeCell';
import { ResponsiveConfig } from './use-responsive-config';
import { domainFavicon } from '../utils';
import { PresenceSegments } from '../types';

// Composant cliquable pour les favicons individuels
const ClickableFavicon = ({ domain, workspaceId, iconSize, theme }: { 
    domain: string; 
    workspaceId?: string; 
    iconSize: number; 
    theme: any; 
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleFaviconClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        // Force la fermeture des tooltips en supprimant les éléments du DOM
        setTimeout(() => {
            const tooltips = document.querySelectorAll('[role="tooltip"]');
            tooltips.forEach(tooltip => tooltip.remove());
        }, 0);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            <Box
                onClick={handleFaviconClick}
                sx={{
                    width: iconSize + 6,
                    height: iconSize + 6,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                        transform: 'scale(1.1)',
                        transition: 'transform 0.2s ease-in-out',
                    },
                }}
            >
                <Box
                    component="img"
                    src={domainFavicon(domain)}
                    sx={{ 
                        width: iconSize + 6, 
                        height: iconSize + 6, 
                        backgroundColor: '#fff', 
                        borderRadius: '50%', 
                        display: 'block', 
                        zIndex: 2 
                    }}
                />
                <Box sx={{ 
                    position: 'absolute', 
                    inset: 0, 
                    borderRadius: '50%', 
                    bgcolor: theme.palette.grey[500], 
                    opacity: 1, 
                    zIndex: 1, 
                    pointerEvents: 'none' 
                }} />
            </Box>
            
            <CompetitorModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                domain={domain}
                workspaceId={workspaceId}
            />
        </>
    );
};

interface DataGridConfigProps {
    responsiveConfig: ResponsiveConfig;
    theme: any;
    t: (key: string) => string;
    workspace?: any;
    sortModel: GridSortModel;
    toggleTrafficShareSort: () => void;
    PresenceRateChart: React.ComponentType<{ data: PresenceSegments; height?: number }>;
    columnType?: 'country' | 'searchTerm';
}

export const createDataGridColumns = ({
    responsiveConfig,
    theme,
    t,
    workspace,
    sortModel,
    toggleTrafficShareSort,
    PresenceRateChart,
    columnType = 'country'
}: DataGridConfigProps): GridColDef[] => {
    // Choisir la première colonne selon le type
    const firstColumn = columnType === 'searchTerm' ? {
        field: 'searchTerm',
        headerName: t('dashboard.global.searchTerm'),
        width: responsiveConfig.searchTermWidth,
        disableColumnMenu: true,
        sortable: true,
        headerClassName: 'searchTermHeader',
        valueGetter: (_value: any, row: any) => Number(row?.trafficShare ?? 0),
        renderHeader: (params: any) => (
            <Box sx={{ display: 'flex', flex: '1 1 auto', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: responsiveConfig.minWidth }}>
                <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.cell, ml: 'auto' }}>
                    <CustomTooltip title={t('dashboard.global.kpiDefinitions.trafficShare') ?? ''} placement="top">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.cell }} onClick={(e: any) => { e.stopPropagation(); toggleTrafficShareSort(); }} aria-label="toggle-sort-traffic-share">
                            <SvgColor
                                src={`/assets/icons/navbar/${theme.palette.mode === 'dark'
                                    ? 'solar--pie-chart-2-bold-duotone-dark-mode'
                                    : 'solar--pie-chart-2-bold-duotone-light-mode'}.svg`}
                                sx={{ width: responsiveConfig.iconSize.chart, height: responsiveConfig.iconSize.chart }}
                            />
                            <IconButton size="small" sx={{ p: 0.25 }}>
                                {sortModel[0]?.field === 'searchTerm'
                                    ? (sortModel[0]?.sort === 'desc'
                                        ? <Iconify icon="iconamoon:arrow-down-2-light" width={responsiveConfig.iconSize.arrow} />
                                        : <Iconify icon="iconamoon:arrow-up-2-light" width={responsiveConfig.iconSize.arrow} />)
                                    : null}
                            </IconButton>
                        </Box>
                    </CustomTooltip>
                </Box>
            </Box>
        ),
        renderCell: (params: any) => {
            const term = params.row?.searchTerm;
            const ts = typeof params.row?.trafficShare === 'number' ? `${Math.round(params.row.trafficShare)}%` : '-';
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2" sx={{ fontSize: responsiveConfig.fontSize.body }}>{term}</Typography>
                    <Typography variant="body2" color="#919EAB" sx={{ fontWeight: 'medium', fontSize: responsiveConfig.fontSize.body }}>{ts}</Typography>
                </Box>
            );
        },
    } : {
        field: 'country',
        headerName: t('dashboard.global.country'),
        width: responsiveConfig.countryWidth,
        disableColumnMenu: true,
        sortable: true,
        valueGetter: (_value: any, row: any) => Number(row?.trafficShare ?? 0),
        renderHeader: (params: any) => (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', minWidth: responsiveConfig.minWidth }}>
                <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.cell }}>
                    <CustomTooltip title={t('dashboard.global.competitiveIntelligence.searchShare') ?? ''} placement="top">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.cell }} onClick={(e: any) => { e.stopPropagation(); toggleTrafficShareSort(); }} aria-label="toggle-sort-traffic-share">
                            <SvgColor
                                src={`/assets/icons/navbar/${theme.palette.mode === 'dark'
                                    ? 'solar--pie-chart-2-bold-duotone-dark-mode'
                                    : 'solar--pie-chart-2-bold-duotone-light-mode'}.svg`}
                                sx={{ width: responsiveConfig.iconSize.chart, height: responsiveConfig.iconSize.chart }}
                            />
                            <IconButton size="small" sx={{ p: 0.25 }}>
                                {sortModel[0]?.field === 'country'
                                    ? (sortModel[0]?.sort === 'desc'
                                        ? <Iconify icon="iconamoon:arrow-down-2-light" width={responsiveConfig.iconSize.arrow} />
                                        : <Iconify icon="iconamoon:arrow-up-2-light" width={responsiveConfig.iconSize.arrow} />)
                                    : null}
                            </IconButton>
                        </Box>
                    </CustomTooltip>
                </Box>
            </Box>
        ),
        renderCell: (params: any) => {
            const countryName = params.row?.country;
            const country = countriesJson?.find((c) => c.name === countryName);
            const flagCode = country ? country.code : null;

            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.column }}>
                        {flagCode ? (
                            <FlagIcon
                                code={flagCode}
                                sx={{ width: responsiveConfig.iconSize.flag, height: responsiveConfig.iconSize.flag, borderRadius: '50%' }}
                            />
                        ) : (
                            <img
                                src="/assets/location.png"
                                alt="location"
                                style={{ width: responsiveConfig.iconSize.flag - 2, height: responsiveConfig.iconSize.flag - 2 }}
                            />
                        )}
                        <Typography variant="body2" sx={{ fontSize: responsiveConfig.fontSize.body }}>{countryName}</Typography>
                    </Box>
                    <Typography variant="body2" color="rgba(99, 115, 129, 1)" sx={{ fontWeight: 'medium', fontSize: responsiveConfig.fontSize.body }}>
                        {typeof params.row?.trafficShare === 'number' ? `${Math.round(params.row.trafficShare)}%` : '-'}
                    </Typography>
                </Box>
            );
        },
    };

    return [
        firstColumn,
    {
        field: 'competitiveLandscape',
        headerName: t('dashboard.competitiveIntelligence.competitivePressure'),
        width: responsiveConfig.competitiveLandscapeWidth,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        cellClassName: 'leftAlignCell',
        valueGetter: (_value: any, row: any) => {
            const raw = (row?.levelRaw as string) ?? '';
            return raw === 'high' ? 3 : raw === 'active' ? 2 : raw === 'low' ? 1 : 0;
        },
        sortComparator: (v1, v2) => {
            const order = { high: 3, active: 2, low: 1 };
            const a = typeof v1 === 'string' ? order[v1 as keyof typeof order] || 0 : v1;
            const b = typeof v2 === 'string' ? order[v2 as keyof typeof order] || 0 : v2;
            return a - b;
        },
        renderHeader: (params) => (
            <CustomTooltip title={t('dashboard.global.kpiDefinitions.competitiveLandscape') ?? ''} placement="top">
                <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
            </CustomTooltip>
        ),
        renderCell: (params) => {
            if (params.row?.status === 'not scrapped') return null;
            return (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', width: '100%' }}>
                    <CompetitiveLandscapeCell
                        level={params.row.levelRaw}
                        brandDomain={params.row.brandDomain}
                        competitorDomain={params.row.firstCompetitorDomain}
                        brandPresenceRate={params.row.paidPresenceRate}
                        competitorPresenceRate={params.row.firstCompetitorPaidPresenceRate}
                        isDemoBrand={params.row.isDemoBrand}
                    />
                </Box>
            );
        },
    },
    {
        field: 'topCompetitors',
        headerName: t('dashboard.competitiveIntelligence.topDomains'),
        flex: 1,
        minWidth: responsiveConfig.topCompetitorsMinWidth,
        sortable: false,
        renderHeader: (params) => (
            <CustomTooltip title={t('dashboard.global.kpiDefinitions.topDomains') ?? ''} placement="top">
                <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
            </CustomTooltip>
        ),
        renderCell: (params) => {
            if (params.row?.status === 'not scrapped') return null;
            const competitors: string[] = Array.isArray(params.value) ? params.value : [];
            const rates: Record<string, number> = params.row.topCompetitorsPaidRates || {};
            const filteredCompetitors: string[] = competitors.filter((c: string) => typeof rates[c] === 'number' && rates[c] > 0);
            const first = filteredCompetitors[0];
            if (!first) return null;
            const brandDomain: string | undefined = params.row?.brandDomain;
            const brandRate: number | null = typeof params.row?.paidPresenceRate === 'number' ? Math.round(params.row.paidPresenceRate) : null;
            // Construire le contenu du tooltip unique avec tous les concurrents
            const tooltipContent = (
                <Box sx={{ minWidth: 280 }}>
                    <Stack direction="column" spacing={1.25}>
                        <Stack 
                          direction="row" 
                          alignItems="center" 
                          justifyContent="space-between" 
                          sx={{ px: 1.5, pt: 1 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontWeight: 700, 
                                fontSize: responsiveConfig.fontSize.caption 
                              }}>
                                  {titleCase('competitors')}
                            </Typography>
                            <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              fontSize: responsiveConfig.fontSize.caption 
                            }}>
                                Paid PR
                            </Typography>
                        </Stack>
                        <Stack direction="column" spacing={0.5}>
                            {brandDomain ? (
                                <Stack
                                    key={`brand-${brandDomain}`}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 1.5,
                                        backgroundColor: theme.palette.mode === 'dark' ? "rgba(145, 158, 171,0.20)" : alpha('#00A76F', 0.1),
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                        <Box
                                            component="img"
                                            src={domainFavicon(brandDomain)}
                                            sx={{
                                                width: responsiveConfig.iconSize.domain,
                                                height: responsiveConfig.iconSize.domain,
                                                backgroundColor: '#fff',
                                                borderRadius: '50%',
                                                display: 'block',
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            noWrap
                                            sx={{
                                                fontSize: responsiveConfig.fontSize.caption,
                                                maxWidth: 180,
                                                fontWeight: 500
                                            }}>
                                            {titleCase(brandDomain).split(".")[0]}
                                        </Typography>
                                    </Stack>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: responsiveConfig.fontSize.caption }}>
                                        {brandRate != null ? `${brandRate}%` : '-'}
                                    </Typography>
                                </Stack>
                            ) : null}
                            {filteredCompetitors.map((competitor: string, index: number) => {
                                const rate = typeof rates[competitor] === 'number' ? Math.round(rates[competitor]) : null;
                                return (
                                    <Stack
                                        key={competitor}
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{
                                            px: 1.5,
                                            py: 0.75,
                                            borderRadius: 1.5,
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                            <Box
                                                component="img"
                                                src={domainFavicon(competitor)}
                                                sx={{
                                                    width: responsiveConfig.iconSize.domain,
                                                    height: responsiveConfig.iconSize.domain,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '50%',
                                                    display: 'block',
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={{ fontSize: responsiveConfig.fontSize.caption, maxWidth: 180, fontWeight: 500 }}>
                                                {titleCase(competitor).split(".")[0]}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: responsiveConfig.fontSize.caption }}>
                                            {rate != null ? `${rate}%` : '-'}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Stack>
                </Box>
            );

            return (
                <CustomTooltip title={tooltipContent} placement="top" background="#fff" disableInteractive>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: responsiveConfig.spacing.column, width: '100%' }}>
                        {first ? (
                            <span>
                                <DomainPill domain={first} workspaceId={workspace?.id} />
                            </span>
                        ) : null}
                        <Box 
                            sx={{ display: 'flex', alignItems: 'center', gap: 0, position: 'relative', height: '100%', alignSelf: 'stretch', justifyContent: 'flex-start' }}
                        >
                            {filteredCompetitors.slice(1).map((competitor: string, index: number) => (
                                <Box
                                    key={`${competitor}-${index}`}
                                    sx={{
                                        zIndex: index + 1,
                                        ml: index === 0 ? 0 : '-3.6px',
                                    }}
                                >
                                    <ClickableFavicon 
                                        domain={competitor} 
                                        workspaceId={workspace?.id}
                                        iconSize={responsiveConfig.iconSize.domain}
                                        theme={theme}
                                    />
                                </Box>
                            ))}
                            {typeof params.row?.otherCompetitors === 'number' && params.row.otherCompetitors > 0 ? (
                                <Box
                                    key='other-pill'
                                    sx={{
                                        zIndex: filteredCompetitors.length + 2,
                                        ml: filteredCompetitors.length > 1 ? '-3.6px' : 0,
                                    }}
                                >
                                        <Box
                                            sx={{
                                                width: responsiveConfig.iconSize.domain + 8,
                                                height: responsiveConfig.iconSize.domain + 8,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: '#637381',
                                                color: theme.palette.mode === 'dark' ? '#fff' : '#111',
                                                fontSize: "13px", 
                                                fontWeight: 600,
                                            }}
                                        >
                                            {`+${params.row.otherCompetitors}`}
                                        </Box>
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                </CustomTooltip>
            );
        },
    },
    {
        field: 'blendedPresenceRate',
        headerName: t('dashboard.global.competitiveIntelligence.blendedVisibilityRate'),
        flex: 1,
        minWidth: responsiveConfig.blendedPresenceMinWidth,
        renderHeader: (params) => (
            <CustomTooltip title={t('dashboard.global.kpiDefinitions.blendedPresenceRate') ?? ''} placement="top">
                <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
            </CustomTooltip>
        ),
        renderCell: (params) => {
            if (params.row?.status === 'not scrapped') return null;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.padding, width: '100%' }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 'medium',
                            minWidth: responsiveConfig.iconSize.flag + 28,
                            maxWidth: responsiveConfig.iconSize.flag + 28,
                            textAlign: 'right', fontSize: responsiveConfig.fontSize.body
                        }}>
                        {typeof params.value === 'number' && !isNaN(params.value) ? Math.round(params.value) : 0}%
                    </Typography>
                    <Box sx={{ flex: '1 1 0', minWidth: 0, width: 0, height: 40, display: 'flex', alignItems: 'center' }}>
                        <PresenceRateChart data={params.row.presenceRateData} height={40} />
                    </Box>
                </Box>
            );
        },
    },
    ];
};
